/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Form, Input, Modal, notification, Row, Select } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import type { ColDef } from 'ag-grid-community'
import SimpleAgGrid from '../../../compnents/simpleTable/SimpleAgGrid'
import { RootDispatch } from '../../../app/store'
import { getMemberPointParticipatoryList, getMemberPointExceptionPage, submitMemberPoint, submitMemberPointException } from '../MembersManagementPageSlice'
import { TreatmentBillingCategories, BillingCategoryName } from '../../../models/billingCategory'

const normalizePoint = (p: any) => (p == null || p === -1 || p === '-1' ? null : p)

// 积分单元格：自管理编辑态，点击直接进入输入，跨行切换无需二次点击
const PointCell = ({ params, onChange }: { params: any; onChange: (id: any, value: any) => void }) => {
  const [selected, setSelected] = useState(!!params?.node?.selected)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const api = params?.api
    const node = params?.node
    if (!api || !node) return
    const sync = () => setSelected(!!node.selected)
    api.addEventListener('selectionChanged', sync)
    return () => api.removeEventListener('selectionChanged', sync)
  }, [params?.api, params?.node])

  if (!selected) return null

  const d = params.data

  const commit = () => {
    const val = (value ?? '').trim()
    if (val === '') {
      setError('请输入积分')
      return
    }
    if (!/^\d+$/.test(val)) {
      setError('请输入大于等于0的整数')
      return
    }
    d.point = val
    onChange(d.id, val)
    setError(null)
    setEditing(false)
  }

  if (editing) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Input
          autoFocus
          size="small"
          value={value}
          style={error ? { borderColor: '#ff4d4f' } : undefined}
          onChange={(e) => {
            setValue(e.target.value)
            if (error) setError(null)
          }}
          onBlur={commit}
          onPressEnter={commit}
        />
        {error ? (
          <div
            style={{
              color: '#ff4d4f',
              fontSize: 12,
              lineHeight: '13px',
              height: 13,
              marginTop: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {error}
          </div>
        ) : null}
      </div>
    )
  }

  const v = d.point
  return (
    <div
      onClick={() => {
        setValue(v == null || v === '' ? '' : `${v}`)
        setError(null)
        setEditing(true)
      }}
      style={{ cursor: 'pointer', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {v == null || v === '' ? <span style={{ color: '#bfbfbf' }}>点击输入积分</span> : `${v}积分`}
    </div>
  )
}

interface AddTreatmentForPointsProps {
  visible: boolean
  onCancel: () => void
  onSuccess?: () => void
  title?: string
  type?: number
  defaultSelectedRowKeys?: React.Key[]
  defaultSelectedRows?: any[]
  exceptionBillingCategory?: string
}

export const AddTreatmentForPoints = ({
  visible,
  onCancel,
  onSuccess,
  title = '添加诊疗',
  type = 1,
  defaultSelectedRowKeys,
  defaultSelectedRows,
  exceptionBillingCategory,
}: AddTreatmentForPointsProps) => {
  const dispatch = useDispatch<RootDispatch>()
  const [form] = Form.useForm()

  const [data, setData] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [pageLoading, setPageLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)

  const [queries, setQueries] = useState<any>({ size: 10, current: 1 })
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<any[]>([])

  const dataRef = useRef(data)
  dataRef.current = data
  const selectedRowsRef = useRef(selectedRows)
  selectedRowsRef.current = selectedRows
  // 积分跨页记忆，避免输入时 setState 导致 Input 失焦
  const pointMapRef = useRef<Record<string, any>>({})

  // 初始化默认选中项
  useEffect(() => {
    if (visible && defaultSelectedRowKeys && defaultSelectedRows) {
      setSelectedRowKeys(defaultSelectedRowKeys)
      setSelectedRows(defaultSelectedRows)
    }
  }, [visible])

  const handleSearch = (): void => {
    const values = form.getFieldsValue()
    setQueries({ ...queries, ...values, current: 1 })
  }

  const handleQuery = () => {
    handleSearch()
  }

  // 获取列表（编辑例外时走例外分页接口，并直接传当前开票项目）
  const getPage = () => {
    setPageLoading(true)
    const request = exceptionBillingCategory
      ? getMemberPointExceptionPage({ ...queries, billingCategory: exceptionBillingCategory, catalogType: 1 })
      : getMemberPointParticipatoryList({ ...queries, catalogType: 1, type })
    dispatch(request)
      .then(unwrapResult)
      .then((res: any) => {
        const defaultMap = new Map<any, any>((defaultSelectedRows || []).map((r: any) => [r.id, r]))
        const records = (res?.records || []).map((r: any) => {
          const def = defaultMap.get(r.id)
          // 已保存行仅用于勾选/保存（id、tenantCatalogId），展示字段以 pageGoods 为准
          const base = def
            ? { ...r, id: def.id ?? r.id, tenantCatalogId: def.tenantCatalogId ?? r.tenantCatalogId }
            : r
          const savedPoint = pointMapRef.current[r.id]
          return {
            ...base,
            point: savedPoint != null && savedPoint !== '' ? savedPoint : normalizePoint(r.point),
          }
        })
        setData(records)
        setTotal(res?.total || 0)
      })
      .finally(() => setPageLoading(false))
  }

  // 弹框打开时（上升沿）重置到第一页
  const prevVisibleRef = useRef(false)
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setQueries({ size: 10, current: 1 })
    }
    prevVisibleRef.current = visible
  }, [visible])

  // 查询条件变化时加载；visible 通过重置 queries 触发，避免打开时重复请求
  useEffect(() => {
    if (visible) {
      getPage()
    }
  }, [queries])

  const handleSelectionChange = (rows: any[], _nodes: any[], ids: any[]) => {
    const currentPageIds = new Set(dataRef.current.map((r: any) => r.id))
    const selectedIds = new Set(ids)

    const newKeys = Array.from(
      new Set([...selectedRowKeys.filter((k) => !currentPageIds.has(k)), ...ids])
    )

    const map = new Map(selectedRowsRef.current.filter(Boolean).map((r: any) => [r.id, r]))
    rows.filter(Boolean).forEach((r: any) => map.set(r.id, r))
    const newRows = Array.from(map.values()).filter((r: any) => {
      if (currentPageIds.has(r.id)) return selectedIds.has(r.id)
      return true
    })

    selectedRowsRef.current = newRows
    setSelectedRowKeys(newKeys)
    setSelectedRows(newRows)
  }

  const handleCellClicked = (event: any) => {
    const colField = event?.colDef?.field
    if (colField === 'checkbox') return
    if (colField === 'point') {
      if (!event.node.selected) {
        event.node.setSelected(true)
      }
      return
    }
    event.node.setSelected(!event.node.selected)
  }

  const handleOk = async () => {
    const rows = selectedRowsRef.current.filter(Boolean).map((r: any) => {
      const p = pointMapRef.current[r.id]
      return p !== undefined ? { ...r, point: p } : r
    })

    if (rows.length === 0) {
      notification.warning({ message: '请至少选择一项' })
      return
    }

    setConfirmLoading(true)
    try {
      if (exceptionBillingCategory) {
        // 编辑例外：直接保存例外数据
        const list = rows.map((r: any) => ({
          tenantCatalogId: r.tenantCatalogId ?? r.id,
          billingCategory: exceptionBillingCategory,
          catalogType: 1,
        }))
        try {
          await dispatch(submitMemberPointException({ memberPointExceptions: list })).then(unwrapResult)
        } catch (e) {
          return
        }
        notification.success({ message: '保存成功' })
        onSuccess?.()
        handleCancel()
        return
      }

      // 添加诊疗：兑换范围需校验积分
      if (type === 0) {
        const hasEmptyPoint = rows.some((r: any) => {
          const p = r.point ?? r.points
          return p == null || p === '' || p === -1 || p === '-1'
        })
        if (hasEmptyPoint) {
          notification.warning({ message: '请填写所有已选项目的积分' })
          return
        }
        const hasInvalidPoint = rows.some((r: any) => {
          const p = String(r.point ?? r.points ?? '')
          return !/^\d+$/.test(p)
        })
        if (hasInvalidPoint) {
          notification.warning({ message: '积分必须为大于等于0的整数' })
          return
        }
      }
      const list = rows.map((r: any) => ({
        isExchange: type,
        name: r.name,
        catalogType: 1,
        tenantCatalogId: r.tenantCatalogId ?? '',
        point: type === 1 ? '' : (r.point ?? r.points ?? ''),
      }))
      try {
        await dispatch(submitMemberPoint(list)).then(unwrapResult)
      } catch (e) {
        return
      }
      notification.success({ message: '保存成功' })
      onSuccess?.()
      handleCancel()
    } finally {
      setConfirmLoading(false)
    }
  }

  const handleCancel = () => {
    setSelectedRowKeys([])
    setSelectedRows([])
    selectedRowsRef.current = []
    pointMapRef.current = {}
    form.resetFields()
    onCancel()
  }

  // 稳定积分列渲染器引用，避免父组件重渲染导致 PointCell 被重建、丢失编辑/校验状态
  const pointCellRenderer = useCallback((params: any) => (
    <PointCell
      params={params}
      onChange={(id: any, value: any) => {
        pointMapRef.current[id] = value
      }}
    />
  ), [])

  const columns: ColDef[] = [
    {
      headerName: '',
      field: 'checkbox',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      pinned: 'left',
      lockPosition: 'left',
      suppressMovable: true,
      resizable: false,
      sortable: false,
      cellStyle: { textAlign: 'center' },
    },
    {
      headerName: '序号',
      width: 60,
      minWidth: 60,
      maxWidth: 80,
      pinned: 'left',
      lockPosition: 'left',
      suppressMovable: true,
      sortable: false,
      resizable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) => {
        if (params.node.rowPinned) return ''
        return params.node?.rowIndex != null
          ? (queries.current - 1) * queries.size + params.node.rowIndex + 1
          : ''
      },
    },
    {
      headerName: '项目名称',
      field: 'name',
      flex: 1,
      minWidth: 160,
      sortable: false,
      cellStyle: { textAlign: 'left' },
      cellRendererFramework: (params: any) => (
        <span style={{ fontWeight: 'bold' }}>{params.value || '-'}</span>
      ),
    },
    {
      headerName: '目录售价',
      field: 'retailPrice',
      width: 120,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) =>
        parseFloat(params.value || 0).toFixed(5),
    },
    {
      headerName: '成本价',
      field: 'stockPrice',
      width: 120,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRendererFramework: (params: any) =>
        params.value >= 0 ? parseFloat(params.value).toFixed(5) : '',
    },
    ...(type === 1
      ? []
      : [
          {
            headerName: '积分',
            field: 'point',
            width: 120,
            sortable: false,
            cellStyle: { textAlign: 'center' },
            cellRendererFramework: pointCellRenderer,
          },
        ]),
  ]

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      maskClosable={false}
      width={1200}
      destroyOnClose
    >
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline" onFinish={handleSearch}>
          <Form.Item name="keyWord" style={{ marginBottom: 0 }}>
            <Input placeholder="诊疗项目名称" allowClear prefix={<SearchOutlined />} style={{ width: 200 }} onChange={handleQuery} />
          </Form.Item>
          {!exceptionBillingCategory && (
            <Form.Item name="billingCategory" style={{ marginBottom: 0 }}>
              <Select placeholder="开票项目" allowClear style={{ width: 160 }} onChange={handleQuery}>
                {TreatmentBillingCategories.map((v) => (
                  <Select.Option value={v} key={v}>{BillingCategoryName(v)}</Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit">查询</Button>
          </Form.Item>
        </Form>
        <span>已选择 {selectedRowKeys.length} 条</span>
      </Row>

      <div style={{ height: 535 }}>
        <SimpleAgGrid
          rowHeight={41}
          isLoading={pageLoading}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          selectedRowKeys={selectedRowKeys}
          getRowId={(r: any) => r.id}
          column={columns}
          page={{
            items: data,
            current: queries?.current || 1,
            size: queries?.size || 10,
            total,
          }}
          onChangePage={(current, pageSize) => {
            setQueries({ ...queries, current, size: pageSize })
          }}
          onSelectionChange={handleSelectionChange}
          onCellClicked={handleCellClicked}
        />
      </div>
    </Modal>
  )
}
